<template>
  <v-dialog v-model="show" width="100vw" scrollable persistent>
    <v-card
      style="max-height: 90vh; display: flex; flex-direction: column"
      class="elevation-0"
    >
      <v-toolbar color="primary" dark flat>
        <v-toolbar-title v-if="bioassaySelected">
          <v-icon left>mdi-table-eye</v-icon>
          Resultados de Bioensayo -
          {{ bioassaySelected.serial || "Sin nombre" }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text
        style="overflow-y: auto; flex: 1 1 auto"
        v-if="bioassaySelected"
      >
        <v-hover v-slot="{ hover }">
          <v-expansion-panels class="my-5">
            <v-expansion-panel
              class="smooth-expand"
              :class="{ 'elevation-6': hover }"
              style="border-left: 4px solid #04c105; transition: all 0.3s ease"
            >
              <v-expansion-panel-header
                class="py-4 px-4 gradient-header"
                :class="{ secondary: !hover, primary: hover }"
                expand-icon="mdi-chevron-down"
                disable-icon-rotate
              >
                <v-row align="center" no-gutters>
                  <v-avatar color="white" size="36" class="mr-3">
                    <v-icon color="primary" size="24">mdi-chart-bubble</v-icon>
                  </v-avatar>
                  <v-col>
                    <h3 class="text-body-1 white--text font-weight-bold mb-1">
                      Información inicial
                    </h3>
                    <span class="caption white--text text--lighten-2">
                      {{
                        bioassaySelected.molecularMarkers.length
                          ? `${bioassaySelected.molecularMarkers.length} marcadores detectados`
                          : "Sin marcadores"
                      }}
                    </span>
                  </v-col>
                  <v-spacer></v-spacer>
                  <v-chip small dark color="accent" class="mr-2">
                    <v-icon left small>mdi-flask</v-icon>
                    {{ bioassaySelected.products.length }} producto(s)
                  </v-chip>
                </v-row>
              </v-expansion-panel-header>

              <v-expansion-panel-content class="animated-content">
                <v-container fluid class="pt-4 px-4">
                  <v-row class="align-center mb-4">
                    <v-col cols="12" md="3" class="py-0">
                      <div class="d-flex align-center">
                        <v-icon color="secondary" class="mr-2">mdi-dna</v-icon>
                        <span
                          class="subtitle-1 font-weight-bold secondary--text"
                          >Biomarcadores</span
                        >
                        <v-chip
                          small
                          color="secondary"
                          text-color="white"
                          class="ml-2"
                        >
                          {{ bioassaySelected.molecularMarkers.length }}
                        </v-chip>
                      </div>
                    </v-col>
                    <v-col cols="12" md="9" class="py-0">
                      <div class="chip-container">
                        <v-tooltip
                          top
                          v-for="(
                            marker, index
                          ) in bioassaySelected.molecularMarkers"
                          :key="index + marker"
                        >
                          <template v-slot:activator="{ on }">
                            <v-chip
                              v-on="on"
                              :color="index % 2 === 0 ? 'primary' : 'secondary'"
                              class="ma-1 animated-chip"
                              label
                              small
                              :outlined="index % 3 === 0"
                              :text-color="
                                index % 3 === 0 ? 'secondary' : 'white'
                              "
                            >
                              <v-icon left small>{{
                                marker.markerType == "MO"
                                  ? "mdi-bacteria"
                                  : "mdi-leaf"
                              }}</v-icon>
                              {{ marker.molecularMarkerID }} -
                              {{
                                marker.markerType == "MO"
                                  ? marker.microorganism_meta.name
                                  : marker.soilFunction.es
                              }}
                            </v-chip>
                          </template>
                          <span>{{
                            marker.markerType == "MO"
                              ? "Taxonomico"
                              : "Función de suelo"
                          }}</span>
                          <template v-if="marker.markerType != 'MO'">
                            <br />
                            <span> Función: {{ marker.soilFunction.es }} </span>
                            <br />
                            <span> Proceso: {{ marker.process.es }} </span>
                          </template>
                        </v-tooltip>
                      </div>
                    </v-col>
                  </v-row>

                  <v-divider
                    class="my-3"
                    style="border-color: rgba(4, 193, 5, 0.2)"
                  ></v-divider>

                  <v-row class="align-center">
                    <v-col cols="12" md="3" class="py-0">
                      <div class="d-flex align-center">
                        <v-icon color="warning" class="mr-2"
                          >mdi-flask-outline</v-icon
                        >
                        <span class="subtitle-1 font-weight-bold warning--text"
                          >Productos</span
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" md="9" class="py-0">
                      <div class="chip-container">
                        <v-tooltip
                          top
                          v-for="(product, index) in bioassaySelected.products"
                          :key="index"
                        >
                          <template v-slot:activator="{ on }">
                            <v-chip
                              v-on="on"
                              :color="index % 2 === 0 ? 'warning' : 'info'"
                              dark
                              class="ma-1 product-chip"
                              pill
                              @click="handleProductSelected(product._id)"
                            >
                              <v-avatar left>
                                <v-img
                                  v-if="product.image"
                                  :src="product.image"
                                ></v-img>
                                <v-icon v-else small
                                  >mdi-bottle-tonic-outline</v-icon
                                >
                              </v-avatar>
                              {{ product.name }}
                              <v-icon right small>mdi-arrow-top-right</v-icon>
                            </v-chip>
                          </template>
                          <span>Ver detalles de {{ product.name }}</span>
                        </v-tooltip>
                      </div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-hover>
        <v-card class="mb-6 elevation-2" v-if="showUploadSection">
          <v-card-title class="subtitle-1 font-weight-bold">
            <v-row>
              <v-col>
                <v-icon left color="primary">mdi-cloud-upload</v-icon>
                Subir resultados por tiempo
              </v-col>
              <v-col class="text-right" cols="auto">
                <v-btn
                  color="green"
                  dark
                  rounded
                  @click="exportData()"
                  :loading="isExporting"
                >
                  <v-icon>mdi-file-excel</v-icon>
                  Exportar resultados
                </v-btn>
              </v-col>
              <v-col class="text-right" cols="auto">
                <v-btn
                  color="primary"
                  rounded
                  :loading="isFinishingResult"
                  :disabled="bioassaySelected.status == 'REVIEW'"
                  @click="showFinishResultConfirmationModal = true"
                >
                  <v-icon>mdi-delete</v-icon>
                  Finalizar resultados
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text class="pa-2">
            <v-row dense>
              <v-col
                cols="12"
                sm="6"
                md="4"
                v-for="(time, index) in fixedTimes"
                :key="time.key"
              >
                <v-card flat outlined class="time-card">
                  <v-card-title class="pa-2 d-flex align-center">
                    <v-chip
                      small
                      :color="time.color"
                      dark
                      class="mr-2 time-chip"
                    >
                      {{ time.label }}
                    </v-chip>

                    <v-menu
                      v-model="time.menu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="time.receptionDate"
                          label="Fecha recepción"
                          prepend-icon="mdi-calendar"
                          readonly
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          dense
                          hide-details
                          :disabled="bioassaySelected.status == 'REVIEW'"
                          @input="handleTimeUpdated(index, time.receptionDate)"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        locale="es"
                        v-model="time.receptionDate"
                        @input="
                          handleTimeUpdated(index, time.receptionDate);
                          time.menu = false;
                        "
                      ></v-date-picker>
                    </v-menu>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          v-bind="attrs"
                          v-on="on"
                          icon
                          small
                          color="red lighten-2"
                          @click="
                            currentIndex = index;
                            showDeleteResultConfirmationModal = true;
                          "
                          :disabled="bioassaySelected.status == 'REVIEW'"
                          :loading="time.isDeleting"
                          class="ml-1"
                        >
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Eliminar resultados tiempo {{ index }}</span>
                    </v-tooltip>
                  </v-card-title>

                  <v-card-text class="pa-2">
                    <v-switch
                      v-model="time.resultReady"
                      :color="time.color"
                      inset
                      label="Resultado listo para curado"
                      hide-details
                      dense
                      :disabled="time.resultReady"
                      @change="handleUpdateResultReady(index)"
                      class="mb-2"
                    ></v-switch>

                    <v-file-input
                      v-model="timeFiles[time.key]"
                      accept=".xlsx"
                      :label="`Archivo ${time.label}`"
                      prepend-icon="mdi-file-excel"
                      outlined
                      dense
                      hide-details
                      :disabled="bioassaySelected.status == 'REVIEW'"
                      :loading="uploading[time.key]"
                      class="mb-2"
                    ></v-file-input>

                    <v-btn
                      color="primary"
                      block
                      small
                      :disabled="!timeFiles[time.key]"
                      :loading="uploading[time.key]"
                      @click="uploadTimeFile(time.key)"
                    >
                      <v-icon left small>mdi-upload</v-icon>
                      Subir {{ time.label }}
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card elevation="2">
          <v-card-title class="subtitle-1 font-weight-bold">
            <v-icon left color="primary">mdi-table</v-icon>
            Resultados registrados
          </v-card-title>
          <v-card-text>
            <div class="table-container">
              <v-data-table
                :headers="fixedHeaders"
                :items="processedResults"
                :items-per-page="10"
                class="elevation-1 results-table"
                dense
                fixed-header
                :height="tableHeight"
                :loading="loading"
                loading-text="Cargando resultados..."
                :footer-props="{
                  'items-per-page-options': [-1],
                }"
              >
                <template #[`item.sample`]="{ item }">
                  <span :class="getSampleClass(item.sample)">
                    {{ item.sample }}
                    <v-tooltip top v-if="isControlSample(item.sample)">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small v-bind="attrs" v-on="on"
                          >mdi-information</v-icon
                        >
                      </template>
                      <span>{{ getControlTooltip(item.sample) }}</span>
                    </v-tooltip>
                  </span>
                </template>

                <template #[`item.marker`]="{ item }">
                  <v-chip :color="item.markerColor" dark>
                    {{ item.marker.name }}
                  </v-chip>
                </template>

                <template #[`item.time0`]="{ item }">
                  <div class="time-cell">
                    <template v-if="item.time0">
                      <div class="d-flex justify-center mb-2">
                        <v-menu offset-y :close-on-content-click="false">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              x-small
                              :color="
                                item.time0.startDna
                                  ? 'primary'
                                  : 'grey lighten-2'
                              "
                              class="clickable-chip"
                            >
                              ADN:
                              {{
                                item.time0.startDna
                                  ? `${item.time0.startDna} ng`
                                  : "Set"
                              }}
                            </v-chip>
                          </template>
                          <v-card>
                            <v-card-text>
                              <v-text-field
                                v-model="item.time0.startDna"
                                label="ADN Inicial ng (T0)"
                                type="number"
                                step="0.0001"
                                dense
                                outlined
                                :disabled="bioassaySelected.status == 'REVIEW'"
                                @keydown.enter="
                                  saveStartDna(
                                    item.marker.name,
                                    item.sample,
                                    'time0',
                                    item.time0.startDna
                                  )
                                "
                              ></v-text-field>
                            </v-card-text>
                          </v-card>
                        </v-menu>
                      </div>
                      <div
                        v-for="(cqValue, repIndex) in item.time0.cq"
                        :key="`time0-${repIndex}`"
                        class="replica-box"
                        :style="{
                          backgroundColor:
                            item.time0.colors[repIndex] || '#f5f5f5',
                          borderLeft: '4px solid #2196F3',
                        }"
                      >
                        <div
                          v-if="item.time0.excluded[repIndex]"
                          class="excluded-replica"
                        >
                          <div>Cq: {{ cqValue || "-" }}</div>
                          <div>
                            Ef: {{ item.time0.efficience[repIndex] || "-" }}
                          </div>
                          <div>
                            R²: {{ item.time0.efficienceR2[repIndex] || "-" }}
                          </div>
                          <v-icon small color="error">mdi-alert-circle</v-icon>
                        </div>
                        <div v-else>
                          <v-checkbox
                            v-model="item.time0.discarded[repIndex]"
                            hide-details
                            class="mt-0 pt-0 mr-1"
                            dense
                            dark
                            color="primary"
                            style="flex: 0 0 24px"
                            label="Descartar resultado"
                            @change="
                              handleResultCurating(
                                item.marker.name,
                                item.sample,
                                'time0',
                                repIndex,
                                item.time0.discarded[repIndex]
                              )
                            "
                          ></v-checkbox>
                          <div class="font-weight-medium">
                            Cq: {{ cqValue || "-" }}
                          </div>
                          <div>
                            Ef: {{ item.time0.efficience[repIndex] || "-" }}
                          </div>
                          <div>
                            R²: {{ item.time0.efficienceR2[repIndex] || "-" }}
                          </div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="no-data">-</div>
                  </div>
                </template>

                <template #[`item.time1`]="{ item }">
                  <div class="time-cell">
                    <template v-if="item.time1">
                      <div class="d-flex justify-center mb-2">
                        <v-menu offset-y :close-on-content-click="false">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              x-small
                              :color="
                                item.time1.startDna
                                  ? 'primary lighten-1'
                                  : 'grey lighten-2'
                              "
                              class="clickable-chip"
                            >
                              ADN:
                              {{
                                item.time1.startDna
                                  ? `${item.time1.startDna} ng`
                                  : "Set"
                              }}
                            </v-chip>
                          </template>
                          <v-card>
                            <v-card-text>
                              <v-text-field
                                v-model="item.time1.startDna"
                                label="ADN Inicial ng (T1)"
                                type="number"
                                step="0.0001"
                                dense
                                outlined
                                :disabled="bioassaySelected.status == 'REVIEW'"
                                @keydown.enter="
                                  saveStartDna(
                                    item.marker.name,
                                    item.sample,
                                    'time1',
                                    item.time1.startDna
                                  )
                                "
                              ></v-text-field>
                            </v-card-text>
                          </v-card>
                        </v-menu>
                      </div>
                      <div
                        v-for="(cqValue, repIndex) in item.time1.cq"
                        :key="`time1-${repIndex}`"
                        class="replica-box"
                        :style="{
                          backgroundColor:
                            item.time1.colors[repIndex] || '#f5f5f5',
                          borderLeft: '4px solid #4CAF50',
                        }"
                      >
                        <div
                          v-if="item.time1.excluded[repIndex]"
                          class="excluded-replica"
                        >
                          <div>Cq: {{ cqValue || "-" }}</div>
                          <div>
                            Ef: {{ item.time1.efficience[repIndex] || "-" }}
                          </div>
                          <div>
                            R²: {{ item.time1.efficienceR2[repIndex] || "-" }}
                          </div>
                          <v-icon small color="error">mdi-alert-circle</v-icon>
                        </div>
                        <div v-else>
                          <v-checkbox
                            v-model="item.time1.discarded[repIndex]"
                            hide-details
                            class="mt-0 pt-0 mr-1"
                            dense
                            dark
                            color="primary"
                            style="flex: 0 0 24px"
                            label="Descartar resultado"
                            @change="
                              handleResultCurating(
                                item.marker.name,
                                item.sample,
                                'time1',
                                repIndex,
                                item.time1.discarded[repIndex]
                              )
                            "
                          ></v-checkbox>
                          <div class="font-weight-medium">
                            Cq: {{ cqValue || "-" }}
                          </div>
                          <div>
                            Ef: {{ item.time1.efficience[repIndex] || "-" }}
                          </div>
                          <div>
                            R²: {{ item.time1.efficienceR2[repIndex] || "-" }}
                          </div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="no-data">-</div>
                  </div>
                </template>

                <template #[`item.time2`]="{ item }">
                  <div class="time-cell">
                    <template v-if="item.time2">
                      <div class="d-flex justify-center mb-2">
                        <v-menu offset-y :close-on-content-click="false">
                          <template v-slot:activator="{ on, attrs }">
                            <v-chip
                              v-bind="attrs"
                              v-on="on"
                              x-small
                              :color="
                                item.time2.startDna
                                  ? 'primary lighten-1'
                                  : 'grey lighten-2'
                              "
                              class="clickable-chip"
                            >
                              ADN:
                              {{
                                item.time2.startDna
                                  ? `${item.time2.startDna} ng`
                                  : "Set"
                              }}
                            </v-chip>
                          </template>
                          <v-card>
                            <v-card-text>
                              <v-text-field
                                v-model="item.time2.startDna"
                                label="ADN Inicial ng (T2)"
                                type="number"
                                step="0.0001"
                                dense
                                outlined
                                clearable
                                :disabled="bioassaySelected.status == 'REVIEW'"
                                @keydown.enter="
                                  saveStartDna(
                                    item.marker.name,
                                    item.sample,
                                    'time2',
                                    item.time2.startDna
                                  )
                                "
                              ></v-text-field>
                            </v-card-text>
                          </v-card>
                        </v-menu>
                      </div>
                      <div
                        v-for="(cqValue, repIndex) in item.time2.cq"
                        :key="`time2-${repIndex}`"
                        class="replica-box"
                        :style="{
                          backgroundColor:
                            item.time2.colors[repIndex] || '#f5f5f5',
                          borderLeft: '4px solid #9C27B0',
                        }"
                      >
                        <div
                          v-if="item.time2.excluded[repIndex]"
                          class="excluded-replica"
                        >
                          <div>Cq: {{ cqValue || "-" }}</div>
                          <div>
                            Ef: {{ item.time2.efficience[repIndex] || "-" }}
                          </div>
                          <div>
                            R²: {{ item.time2.efficienceR2[repIndex] || "-" }}
                          </div>
                          <v-icon small color="error">mdi-alert-circle</v-icon>
                        </div>
                        <div v-else>
                          <v-checkbox
                            v-model="item.time2.discarded[repIndex]"
                            hide-details
                            class="mt-0 pt-0 mr-1"
                            dense
                            dark
                            color="primary"
                            style="flex: 0 0 24px"
                            label="Descartar resultado"
                            @change="
                              handleResultCurating(
                                item.marker.name,
                                item.sample,
                                'time2',
                                repIndex,
                                item.time2.discarded[repIndex]
                              )
                            "
                          ></v-checkbox>
                          <div class="font-weight-medium">
                            Cq: {{ cqValue || "-" }}
                          </div>
                          <div>
                            Ef: {{ item.time2.efficience[repIndex] || "-" }}
                          </div>
                          <div>
                            R²: {{ item.time2.efficienceR2[repIndex] || "-" }}
                          </div>
                        </div>
                      </div>
                    </template>
                    <div v-else class="no-data">-</div>
                  </div>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
    <DeleteResultConfirmationModal
      v-model="showDeleteResultConfirmationModal"
      @confirmation="handleDeleteResults"
      title="¿Está seguro de que desea eliminar los resultados?"
    />
    <ProductModal
      v-model="showProductModal"
      v-if="showProductModal"
      @close="handleProductClose"
    />
    <FinishResultConfirmationModal
      v-model="showFinishResultConfirmationModal"
      @confirmation="handleFinishResult"
      title="¿Está seguro de que desea finalizar los resultados?"
    />
  </v-dialog>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import DeleteResultConfirmationModal from "../../utils/GenericConfirmationModal.vue";
import FinishResultConfirmationModal from "../../utils/GenericConfirmationModal.vue";
import ProductModal from "../../products/modals/ProductModal.vue";
import xlsx from "json-as-xlsx";
export default {
  components: {
    DeleteResultConfirmationModal,
    ProductModal,
    FinishResultConfirmationModal,
  },
  props: {
    value: Boolean,
    showUploadSection: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fixedTimes: [
        {
          key: "time0",
          label: "Tiempo 0",
          color: "#2196F3",
          receptionDate: null,
          menu: null,
          resultReady: false,
          isDeleting: false,
        },
        {
          key: "time1",
          label: "Tiempo 1",
          color: "#4CAF50",
          receptionDate: null,
          menu: null,
          resultReady: false,
          isDeleting: false,
        },
        {
          key: "time2",
          label: "Tiempo 2",
          color: "#9C27B0",
          receptionDate: null,
          menu: null,
          resultReady: false,
          isDeleting: false,
        },
      ],
      fixedHeaders: [
        { text: "Muestra", value: "sample", width: "180px", sortable: true },
        { text: "Marcador", value: "marker", width: "180px", sortable: true },
        { text: "Tiempo 0", value: "time0", width: "220px" },
        { text: "Tiempo 1", value: "time1", width: "220px" },
        { text: "Tiempo 2", value: "time2", width: "220px" },
      ],
      timeFiles: {
        time0: null,
        time1: null,
        time2: null,
      },
      uploading: {
        time0: false,
        time1: false,
        time2: false,
      },
      loading: false,
      processedResults: [],
      times: [
        {
          days: 0,
          sendDate: null,
          ufc: null,
          observations: "",
          isCompleted: false,
        },
        {
          days: 10,
          sendDate: null,
          ufc: null,
          observations: "",
          isCompleted: false,
        },
        {
          days: 20,
          sendDate: null,
          ufc: null,
          observations: "",
          isCompleted: false,
        },
      ],
      initialColors: [],
      isExporting: false,
      showDeleteResultConfirmationModal: false,
      showProductModal: false,
      showFinishResultConfirmationModal: false,
      isFinishingResult: false,
      currentIndex: null,
    };
  },
  computed: {
    ...mapState("bioassays", ["bioassaySelected"]),
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    tableHeight() {
      return this.showUploadSection ? "60vh" : "70vh";
    },
  },
  watch: {
    bioassaySelected: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.results) {
          this.processResultsData();
        }
      },
    },
  },
  methods: {
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),
    ...mapActions("bioassays", [
      "uploadResultBioassay",
      "deleteResultBioassay",
      "updateTimes",
      "updateDnaResult",
      "finishResultBioassay",
      "updateTimeResultStage",
      "discardDataResult",
    ]),
    ...mapMutations("bioassays", ["SET_PRODUCT_ID", "SET_BIOASSAY"]),

    processResultsData() {
      this.loading = true;
      try {
        for (let index = 0; index < this.times.length; index++) {
          this.times[index].sendDate =
            this.bioassaySelected.times[index]?.sendDate;
          this.times[index].ufc =
            this.bioassaySelected.times[index]?.concentration;
          this.times[index].observations =
            this.bioassaySelected.times[index]?.observations;
          this.times[index].isCompleted =
            this.bioassaySelected.times[index]?.isCompleted || false;
          this.times[index].receptionDate =
            this.bioassaySelected.times[index]?.receptionDate;
          this.times[index].resultReady =
            this.bioassaySelected.times[index]?.resultReady;
          // fixed times
          this.fixedTimes[index].receptionDate =
            this.bioassaySelected.times[index]?.receptionDate;
          this.fixedTimes[index].resultReady =
            this.bioassaySelected.times[index]?.resultReady;
        }

        const resultsBySampleMarker = {};
        if (this.initialColors.length != this.bioassaySelected.results.length) {
          for (let index = 0; index < this.bioassaySelected.results; index++) {
            let markerColor = this.generateColorHex();
            this.initialColors.push(markerColor);
          }
        }

        this.bioassaySelected.results.forEach((markerData, index) => {
          let color = this.initialColors[index];
          markerData.data.forEach((sampleData) => {
            const key = `${sampleData.name}_${markerData.marker.name}`;

            if (!resultsBySampleMarker[key]) {
              resultsBySampleMarker[key] = {
                sample: sampleData.name,
                marker: markerData.marker,
                markerColor: color,
                id: key,
              };
            }
            this.fixedTimes.forEach((time) => {
              if (sampleData[time.key]) {
                resultsBySampleMarker[key][time.key] = sampleData[time.key];
              }
            });
          });
        });

        this.processedResults = Object.values(resultsBySampleMarker);
      } catch (error) {
        console.error("Error processing results:", error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: "Error al procesar los resultados",
          timeout: 5000,
        });
      } finally {
        this.loading = false;
      }
    },

    async uploadTimeFile(timeKey) {
      const file = this.timeFiles[timeKey];
      if (!file) return;

      this.uploading[timeKey] = true;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("bioassayId", this.bioassaySelected._id);
      formData.append("time", timeKey);

      try {
        const response = await this.uploadResultBioassay(formData);
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Datos para ${this.getTimeLabel(
            timeKey
          )} subidos correctamente`,
          timeout: 5000,
        });
        this.timeFiles[timeKey] = null;
      } catch (error) {
        console.error(`Error uploading ${timeKey}:`, error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Error al subir datos para ${this.getTimeLabel(timeKey)}: ${
            error.message
          }`,
          timeout: 5000,
        });
      } finally {
        this.uploading[timeKey] = false;
      }
    },

    getTimeLabel(timeKey) {
      return this.fixedTimes.find((t) => t.key === timeKey)?.label || timeKey;
    },

    getSampleClass(sample) {
      if (sample.includes("Control negativo")) return "control-negative";
      if (sample.includes("Control positivo")) return "control-positive";
      // if (sample.includes("Suelo")) return "soil-control";
      if (sample.match(/D\d+\.\d+/)) return "dose-sample";
      return "";
    },

    isControlSample(sample) {
      return (
        sample.includes("Control negativo") ||
        sample.includes("Control positivo") ||
        sample.includes("Suelo")
      );
    },

    getControlTooltip(sample) {
      if (sample.includes("Control negativo"))
        return "Control negativo del experimento";
      if (sample.includes("Control positivo"))
        return "Control positivo del experimento";
      if (sample.includes("Suelo")) return "Suelo control de referencia";
      return "";
    },
    generateColorHex() {
      const baseR = Math.floor(Math.random() * 256);
      const baseG = Math.floor(Math.random() * 256);
      const baseB = Math.floor(Math.random() * 256);

      const r = Math.floor((baseR + 255) / 2);
      const g = Math.floor((baseG + 255) / 2);
      const b = Math.floor((baseB + 255) / 2);

      const colorHex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");

      return `#${colorHex.toUpperCase()}`;
    },
    async handleDeleteResults() {
      try {
        this.times[this.currentIndex].isDeleting = true;
        let key = "";
        switch (this.currentIndex) {
          case 0:
            key = "time0";
            break;
          case 1:
            key = "time1";
            break;
          case 2:
            key = "time2";
            break;
        }
        let params = {
          bioassayId: this.bioassaySelected._id,
          key: key,
        };
        const response = await this.deleteResultBioassay({
          body: {},
          params,
        });
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Resultados eliminados correctamente`,
          timeout: 5000,
        });
      } catch (error) {
        console.error(`Error deleting:`, error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Error al eliminar datos: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.currentIndex = null;
        this.times[this.currentIndex].isDeleting = false;
      }
    },
    async handleTimeUpdated(index, newValue) {
      try {
        let payload = {
          data: this.times.map((element, innerIndex) => {
            return {
              sendDate: element.sendDate || null,
              concentration: element.ufc || null,
              observations: element.observations || null,
              isCompleted: innerIndex == index || element.isCompleted,
              receptionDate:
                innerIndex == index ? newValue : element.receptionDate,
              resultReady: element.resultReady || null,
            };
          }),
        };
        let params = {
          bioassayId: this.bioassaySelected._id,
        };
        const response = await this.updateTimes({
          body: payload,
          params,
        });
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Información guardada`,
          timeout: 3000,
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },
    handleProductSelected(productId) {
      this.SET_PRODUCT_ID(productId);
      this.showProductModal = true;
    },
    handleProductClose() {
      this.showProductModal = false;
      this.SET_PRODUCT_ID(null);
    },
    async saveStartDna(marker, sample, timeKey, value) {
      try {
        let payload = {
          marker,
          sample,
          time: timeKey,
          startDna: value,
        };
        let params = {
          bioassayId: this.bioassaySelected._id,
        };
        const response = await this.updateDnaResult({
          body: payload,
          params,
        });
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Información guardada`,
          timeout: 3000,
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },
    async exportData() {
      try {
        this.isExporting = true;
        let columns = [
          { label: "Marcador molecular", value: "marker" },
          { label: "Muestra", value: "sample" },

          { label: "ng totales (T0)", value: "time0_ng" },
          { label: "Cq (T0)", value: "time0_cq" },
          { label: "Eficiencia (T0)", value: "time0_efficiency" },
          { label: "R² (T0)", value: "time0_r2" },

          { label: "ng totales (T1)", value: "time1_ng" },
          { label: "Cq (T1)", value: "time1_cq" },
          { label: "Eficiencia (T1)", value: "time1_efficiency" },
          { label: "R² (T1)", value: "time1_r2" },

          { label: "ng totales (T2)", value: "time2_ng" },
          { label: "Cq (T2)", value: "time2_cq" },
          { label: "Eficiencia (T2)", value: "time2_efficiency" },
          { label: "R² (T2)", value: "time2_r2" },
        ];

        let data = [];
        this.bioassaySelected.results.forEach((result) => {
          const markerName = result.marker.name;

          result.data.forEach((sample) => {
            const maxReplicas = Math.max(
              sample.time0.cq?.length || 0,
              sample.time1.cq?.length || 0,
              sample.time2.cq?.length || 0,
              1
            );

            for (let i = 0; i < maxReplicas; i++) {
              let isDiscarded = false;
              isDiscarded = sample.time0.discarded?.[i];
              if (!isDiscarded) {
                isDiscarded = sample.time1.discarded?.[i];
              }

              if (!isDiscarded) {
                isDiscarded = sample.time2.discarded?.[i];
              }

              if (isDiscarded) {
                const row = {
                  marker: markerName,
                  sample: sample.name,

                  time0_ng: sample.time0.startDna ?? "",
                  time0_cq: "NA",
                  time0_efficiency: "NA",
                  time0_r2: "NA",

                  time1_ng: sample.time1.startDna ?? "",
                  time1_cq: "NA",
                  time1_efficiency: "NA",
                  time1_r2: "NA",

                  time2_ng: sample.time2.startDna ?? "",
                  time2_cq: "NA",
                  time2_efficiency: "NA",
                  time2_r2: "NA",
                };
                data.push(row);
              } else {
                const row = {
                  marker: markerName,
                  sample: sample.name,

                  time0_ng: sample.time0.startDna ?? "",
                  time0_cq: sample.time0.cq?.[i] ?? "",
                  time0_efficiency: sample.time0.efficience?.[i] ?? "",
                  time0_r2: sample.time0.efficienceR2?.[i] ?? "",

                  time1_ng: sample.time1.startDna ?? "",
                  time1_cq: sample.time1.cq?.[i] ?? "",
                  time1_efficiency: sample.time1.efficience?.[i] ?? "",
                  time1_r2: sample.time1.efficienceR2?.[i] ?? "",

                  time2_ng: sample.time2.startDna ?? "",
                  time2_cq: sample.time2.cq?.[i] ?? "",
                  time2_efficiency: sample.time2.efficience?.[i] ?? "",
                  time2_r2: sample.time2.efficienceR2?.[i] ?? "",
                };
                data.push(row);
              }
            }
          });
        });

        const settings = {
          fileName: `Resultados Bioensayo ${
            this.bioassaySelected.serial
          } - ${new Date().toLocaleDateString().replaceAll("/", "-")}`,
          extraLength: 3,
          writeOptions: {},
        };

        const report = [
          {
            sheet: "Resultados",
            columns: columns,
            content: data,
          },
        ];
        xlsx(report, settings);

        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: "Información exportada",
          timeout: 4000,
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.isExporting = false;
      }
    },
    async handleFinishResult() {
      try {
        this.isFinishingResult = true;
        let payload = {};
        let params = {
          bioassayId: this.bioassaySelected._id,
        };
        const response = await this.finishResultBioassay({
          body: payload,
          params,
        });
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Resultados actualizados`,
          timeout: 3000,
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      } finally {
        this.isFinishingResult = false;
      }
    },
    async handleUpdateResultReady(index) {
      try {
        let payload = {};
        let params = {
          bioassayId: this.bioassaySelected._id,
          outerIndex: index,
        };
        const response = await this.updateTimeResultStage({
          body: payload,
          params,
        });
        this.SET_BIOASSAY(response.bioassays);
        this.times[index].resultReady =
          this.bioassaySelected.times[index]?.resultReady;
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Información guardada`,
          timeout: 3000,
        });
      } catch (error) {
        console.log(error);
        this.fixedTimes[index].resultReady = false;
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },
    async handleResultCurating(marker, sample, timeKey, index, newValue) {
      try {
        let payload = {
          marker,
          sample,
          time: timeKey,
          valueIndex: index,
          value: newValue,
        };
        let params = {
          bioassayId: this.bioassaySelected._id,
        };
        const response = await this.discardDataResult({
          body: payload,
          params,
        });
        this.SET_BIOASSAY(response.bioassays);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "success",
          text: `Información guardada`,
          timeout: 3000,
        });
      } catch (error) {
        console.log(error);
        this.SET_ALERT_TYPE({
          visible: true,
          type: "error",
          text: `Algo salió mal. Error: ${error.message}`,
          timeout: 5000,
        });
      }
    },
  },
};
</script>

<style scoped>
.table-container {
  padding: 8px;
}

.results-table {
  border-radius: 8px;
  overflow: hidden;
}

.control-negative {
  font-weight: 600;
  color: #f44336;
}

.control-positive {
  font-weight: 600;
  color: #4caf50;
}

.soil-control {
  font-weight: 600;
  color: #9c27b0;
}

.dose-sample {
  font-weight: 500;
  color: #2196f3;
}

.time-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 200px;
}

.replica-box {
  padding: 6px;
  border-radius: 4px;
  margin: 2px 0;
  color: white;
  font-size: 0.75rem;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}

.excluded-replica {
  text-decoration: line-through;
  opacity: 0.7;
}

.no-data {
  color: #999;
  font-style: italic;
  text-align: center;
  padding: 8px;
}

.v-data-table.results-table::v-deep th {
  background-color: #f5f5f5;
  position: sticky;
  top: 0;
  z-index: 2;
  font-weight: 600;
}

.v-data-table.results-table::v-deep tr:nth-child(even) {
  background-color: #fafafa;
}

.v-data-table.results-table::v-deep tr:hover {
  background-color: #f0f0f0;
}

.v-data-table.results-table::v-deep .v-data-table-header th {
  white-space: nowrap;
}

.v-data-table.results-table::v-deep .v-data-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.gradient-header {
  background: linear-gradient(
    45deg,
    var(--v-secondary-base),
    var(--v-primary-base)
  );
  transition: all 0.3s ease;
}

.animated-chip {
  transition: transform 0.2s;
}
.animated-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);
}

.product-chip {
  transition: all 0.3s ease;
}
.product-chip:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.animated-content {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chip-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.smooth-expand {
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.time-cell {
  min-width: 140px;
}

.clickable-chip {
  cursor: pointer;
  min-width: 80px;
  justify-content: center;
  font-size: 0.75rem;
  margin-bottom: 4px;
}

.time-card {
  transition: all 0.3s ease;
}
.time-card:hover {
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.1);
}
.time-chip {
  min-width: 60px;
  justify-content: center;
}
</style>
